import { useEffect, useState } from 'react';
import { Download, FileText, Loader2, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@components/ui/dialog';
import { getSummary } from '@generated/server/summary';
import { useSession } from './session';
import { buildPdf, buildWordHtml, downloadBlob, type SummaryData } from '@generated/lib/summary-export';

export function SummaryPreviewModal({ lib, id, onClose }: { lib: 'itp' | 'sd'; id: string; onClose: () => void }) {
  const { sid } = useSession();
  const [data, setData] = useState<SummaryData | null>(null);
  const [url, setUrl] = useState('');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<'' | 'pdf' | 'docx'>('');

  useEffect(() => {
    let created = '';
    let active = true;
    (async () => {
      try {
        const s = await getSummary({ data: { sid, lib, id, kind: 'preview' } });
        if (!active || !s) return;
        setData(s);
        const b = await buildPdf(s);
        if (!active) return;
        created = URL.createObjectURL(b);
        setBlob(b);
        setUrl(created);
      } catch (e) {
        console.error(e);
        if (active) setError(e instanceof Error ? e.message : 'Failed to generate preview.');
      }
    })();
    return () => { active = false; if (created) URL.revokeObjectURL(created); };
  }, [sid, lib, id]);

  async function download(kind: 'pdf' | 'docx') {
    if (!data) return;
    setBusy(kind);
    try {
      await getSummary({ data: { sid, lib, id, kind } });
      if (kind === 'pdf' && blob) downloadBlob(blob, `decision-${data.name}-summary.pdf`);
      if (kind === 'docx') downloadBlob(new Blob(['\ufeff', buildWordHtml(data)], { type: 'application/msword' }), `decision-${data.name}-summary.doc`);
    } catch (e) {
      console.error(e);
      setError('The export could not be completed.');
    } finally {
      setBusy('');
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent showCloseButton={false} className="flex h-[90vh] w-full max-w-4xl flex-col gap-0 overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-2 bg-secondary px-5 py-3 text-secondary-foreground">
          <DialogTitle className="min-w-0 flex-1 truncate text-sm font-semibold">
            Decision Summary{data && <span className="ml-2 font-normal opacity-60">— {data.name}</span>}
            <span className="ml-3 text-xs font-normal opacity-50">Using default layout</span>
          </DialogTitle>
          <button disabled={!blob || !!busy} onClick={() => download('pdf')} className="flex h-9 items-center gap-1.5 rounded-lg border border-white/30 bg-white/15 px-3 text-sm hover:bg-white/25 disabled:opacity-50">
            {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Download PDF
          </button>
          <button disabled={!data || !!busy} onClick={() => download('docx')} className="flex h-9 items-center gap-1.5 rounded-lg border border-white/30 bg-white/15 px-3 text-sm hover:bg-white/25 disabled:opacity-50">
            {busy === 'docx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}Download Word
          </button>
          <button onClick={onClose} aria-label="Close" className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-white/15"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-auto bg-[hsl(var(--preview))]">
          {error ? <div className="flex h-full items-center justify-center text-sm text-destructive">{error}</div>
            : !url ? <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-8 w-8 animate-spin text-secondary/40" />Generating preview…</div>
            : <iframe src={`${url}#toolbar=0&navpanes=0`} title="Summary preview" className="h-full w-full border-0" />}
        </div>
        <div className="border-t border-border bg-muted px-5 py-2.5 text-xs text-muted-foreground">Only the decision summary can be exported — the full decision remains view-only.</div>
      </DialogContent>
    </Dialog>
  );
}
